import React from 'react';
import styled from 'styled-components';
import { MarginProps } from 'styled-system';

import { TwoTilesBlockData } from 'api/cms';
import { FilestackImageData } from 'api/types/shared.types';
import Box from 'components/Box';
import { TrackedPrimaryButtonLink } from 'components/Button';
import { Body, H2 } from 'components/Text';
import Tile from 'components/Tile';
import { colors } from 'theme';

const BlockTile = styled(
  ({
    buttonText,
    buttonUrl,
    className,
    dataQaPrefix,
    headline,
    text,
    blockIndex,
    pageUpdated,
    ...props
  }: {
    buttonText: string;
    buttonUrl: string;
    className?: string;
    dataQaPrefix: string;
    headline: string;
    text: string;
    image?: FilestackImageData;
    blockIndex: number;
    pageUpdated: string;
  }) => (
    <Tile
      position="relative"
      display="flex"
      p={['20px', null, '60px']}
      alignItems="center"
      justifyContent="stretch"
      flexDirection="column"
      flex="1"
      overflow="hidden"
      textAlign="center"
      className={className}
      data-qa-prefix={dataQaPrefix}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      <H2 m="0">{headline}</H2>
      <Box mt="20px" width="100%" flex="1 1 auto">
        <Body
          analyticsData={{
            eventName: 'CMS Block',
            eventProperties: {
              block_position: blockIndex,
              block_type: 'Two Tiles',
            },
          }}
          dangerouslySetInnerHTML={{
            __html: text,
          }}
        />
      </Box>
      {buttonText && buttonUrl && (
        <Box mt={['32px', null, '52px']} width="100%">
          <TrackedPrimaryButtonLink
            href={buttonUrl}
            eventName="CMS Block"
            eventProperties={{
              block_position: blockIndex,
              block_type: 'Two Tiles',
              cta_type: 'Button',
              button_text: buttonText,
              page_updated: pageUpdated,
            }}
          >
            {buttonText}
          </TrackedPrimaryButtonLink>
        </Box>
      )}
    </Tile>
  )
)<MarginProps>`
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 10px;
    background-image: linear-gradient(
      to left,
      ${colors.babyBlue},
      ${colors.waterBlue}
    );
    background-size: 100% 10px;
    background-repeat: no-repeat;
  }
`;

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const TwoTilesBlock = ({
  data: {
    tileOneHeadline,
    tileOneText,
    tileOneButtonText,
    tileOneButtonUrl,
    tileTwoHeadline,
    tileTwoText,
    tileTwoButtonText,
    tileTwoButtonUrl,
  },
  blockIndex,
  pageUpdated,
}: {
  data: TwoTilesBlockData;
  blockIndex: number;
  pageUpdated: string;
}) => (
  <Box
    display="flex"
    width="100%"
    alignItems="stretch"
    flexDirection={['column', 'column', 'row']}
    justifyContent="center"
  >
    <BlockTile
      dataQaPrefix="tile-one"
      headline={tileOneHeadline}
      text={tileOneText}
      buttonText={tileOneButtonText}
      buttonUrl={tileOneButtonUrl}
      blockIndex={blockIndex}
      pageUpdated={pageUpdated}
    />
    <BlockTile
      mt={['20px', null, 0]}
      ml={[null, null, '40px']}
      dataQaPrefix="tile-two"
      headline={tileTwoHeadline}
      text={tileTwoText}
      buttonText={tileTwoButtonText}
      buttonUrl={tileTwoButtonUrl}
      blockIndex={blockIndex}
      pageUpdated={pageUpdated}
    />
  </Box>
);

export default TwoTilesBlock;
